<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between">
          <div class="d-flex align-center mb-4 mb-sm-0">
            <v-avatar size="56" class="mr-4 primary">
              <v-icon size="32" color="white">mdi-folder-cog</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h5 text-sm-h4 mb-1 font-weight-bold">Resources Administration</h1>
              <p class="subtitle-1 grey--text text--darken-1 mb-0">
                Manage downloadable resources for members
              </p>
            </div>
          </div>
          <v-btn
            color="primary"
            large
            class="align-self-stretch align-self-sm-center flex-shrink-0"
            @click="openAddDialog"
          >
            <v-icon left>mdi-plus</v-icon>
            Add Resource
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-card v-if="loading" class="mb-4" elevation="2">
      <v-card-text class="pa-6">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
            class="mb-4"
          ></v-progress-circular>
          <div class="title">Loading resources...</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Resources Table -->
    <v-card v-else elevation="2">
      <v-card-title>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          class="mr-4"
        ></v-text-field>
        <v-spacer></v-spacer>
        <span class="caption grey--text">{{ resources.length }} resources</span>
      </v-card-title>
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        :headers="headers"
        :items="resources"
        :search="search"
        :items-per-page="10"
        class="elevation-0"
      >
        <template v-slot:item.title="{ item }">
          <strong>{{ item.title }}</strong>
        </template>
        <template v-slot:item.language="{ item }">
          <v-chip x-small :color="item.language === 'en' ? 'blue' : 'green'" text-color="white">
            {{ item.language === 'en' ? 'English' : 'Spanish' }}
          </v-chip>
        </template>
        <template v-slot:item.fileSize="{ item }">
          {{ formatFileSize(item.fileSize) }}
        </template>
        <template v-slot:item.isActive="{ item }">
          <v-chip x-small :color="item.isActive ? 'success' : 'error'" text-color="white">
            {{ item.isActive ? 'Active' : 'Inactive' }}
          </v-chip>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A' }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            title="View"
            @click="viewResource(item)"
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            title="Download"
            :loading="downloading === item._id"
            @click="downloadResource(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            title="Edit"
            @click="editResource(item)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="error"
            title="Delete"
            @click="deleteResource(item)"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Mobile: clean stacked cards instead of the cramped native table -->
      <div v-else class="pa-3">
        <v-card
          v-for="item in filteredResources"
          :key="item._id"
          outlined
          class="mb-3"
        >
          <v-card-text class="pb-2">
            <div class="d-flex align-start mb-2">
              <v-icon color="primary" class="mr-2 mt-1">mdi-file-document</v-icon>
              <span class="font-weight-bold body-1">{{ item.title }}</span>
            </div>
            <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
              <v-chip x-small :color="item.language === 'en' ? 'blue' : 'green'" text-color="white">
                {{ item.language === 'en' ? 'English' : 'Spanish' }}
              </v-chip>
              <v-chip x-small :color="item.isActive ? 'success' : 'error'" text-color="white">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </v-chip>
            </div>
            <div class="caption grey--text text--darken-1">{{ item.originalFileName }}</div>
            <div class="caption grey--text">
              {{ formatFileSize(item.fileSize) }}
              <span v-if="item.createdAt"> · {{ new Date(item.createdAt).toLocaleDateString() }}</span>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-btn text small color="primary" @click="viewResource(item)">
              <v-icon left small>mdi-open-in-new</v-icon>View
            </v-btn>
            <v-btn text small color="primary" :loading="downloading === item._id" @click="downloadResource(item)">
              <v-icon left small>mdi-download</v-icon>Download
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn icon small title="Edit" @click="editResource(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="error" title="Delete" @click="deleteResource(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
        <div v-if="filteredResources.length === 0" class="text-center pa-6 grey--text">
          No resources found.
        </div>
      </div>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editingResource ? 'Edit Resource' : 'Add Resource' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="formData.title"
              label="Title *"
              required
              :rules="[v => !!v || 'Title is required']"
              outlined
              dense
              class="mb-3"
            ></v-text-field>

            <v-select
              v-model="formData.language"
              :items="languages"
              label="Language *"
              required
              :rules="[v => !!v || 'Language is required']"
              outlined
              dense
              class="mb-3"
            ></v-select>

            <v-text-field
              v-model="formData.order"
              label="Order (for sorting)"
              type="number"
              outlined
              dense
              class="mb-3"
            ></v-text-field>

            <v-switch
              v-model="formData.isActive"
              label="Active"
              class="mb-3"
            ></v-switch>

            <v-file-input
              v-model="formData.file"
              label="File *"
              :rules="editingResource ? [] : [v => !!v || 'File is required']"
              outlined
              dense
              show-size
              accept="*/*"
              class="mb-3"
            ></v-file-input>

            <v-alert v-if="editingResource && !formData.file" type="info" text dense>
              Leave file empty to keep the current file
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveResource" :loading="saving">
            {{ editingResource ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Delete Resource</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ resourceToDelete && resourceToDelete.title ? resourceToDelete.title : '' }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleting">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ResourcesAdmin',
  data() {
    return {
      resources: [],
      loading: false,
      search: '',
      headers: [
        { text: 'Title', value: 'title' },
        { text: 'Language', value: 'language' },
        { text: 'File Name', value: 'originalFileName' },
        { text: 'File Size', value: 'fileSize' },
        { text: 'Order', value: 'order' },
        { text: 'Status', value: 'isActive' },
        { text: 'Created', value: 'createdAt', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      dialog: false,
      editingResource: null,
      valid: false,
      saving: false,
      formData: {
        title: '',
        language: 'en',
        order: 0,
        isActive: true,
        file: null
      },
      languages: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' }
      ],
      deleteDialog: false,
      resourceToDelete: null,
      deleting: false,
      downloading: null
    };
  },
  computed: {
    // Mobile cards don't use the table's :search, so filter here across the
    // fields shown on the card.
    filteredResources() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.resources;
      return this.resources.filter((r) => [r.title, r.originalFileName, r.language]
        .some((f) => String(f || '').toLowerCase().includes(q)));
    }
  },
  mounted() {
    this.loadResources();
  },
  methods: {
    // Open the resource inline in a new browser tab (cookie-based auth).
    viewResource(resource) {
      window.open(`/api/resources/${resource._id}/download?inline=1`, '_blank', 'noopener');
    },
    async downloadResource(resource) {
      this.downloading = resource._id;
      try {
        const response = await axios.get(`/api/resources/${resource._id}/download`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', resource.originalFileName || resource.fileName || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading resource:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Failed to download resource');
        }
      } finally {
        this.downloading = null;
      }
    },
    async loadResources() {
      this.loading = true;
      try {
        const response = await axios.get('/api/resources/admin');
        this.resources = response.data || [];
      } catch (error) {
        console.error('Error loading resources:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Failed to load resources');
        }
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.editingResource = null;
      this.formData = {
        title: '',
        language: 'en',
        order: 0,
        isActive: true,
        file: null
      };
      this.dialog = true;
    },
    editResource(resource) {
      this.editingResource = resource;
      this.formData = {
        title: resource.title,
        language: resource.language,
        order: resource.order || 0,
        isActive: resource.isActive,
        file: null
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.editingResource = null;
      if (this.$refs.form && this.$refs.form.reset) {
        this.$refs.form.reset();
      }
    },
    async saveResource() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.editingResource && !this.formData.file) {
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Please select a file');
        }
        return;
      }

      this.saving = true;
      try {
        const formData = new FormData();
        formData.append('title', this.formData.title);
        formData.append('language', this.formData.language);
        formData.append('order', this.formData.order);
        formData.append('isActive', this.formData.isActive);
        
        if (this.formData.file) {
          formData.append('file', this.formData.file);
        }

        if (this.editingResource) {
          await axios.put(`/api/resources/admin/${this.editingResource._id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          if (this.$toast && this.$toast.success) {
            this.$toast.success('Resource updated successfully');
          }
        } else {
          await axios.post('/api/resources/admin', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          if (this.$toast && this.$toast.success) {
            this.$toast.success('Resource created successfully');
          }
        }

        this.closeDialog();
        this.loadResources();
      } catch (error) {
        console.error('Error saving resource:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error((error.response && error.response.data && error.response.data.error) || 'Failed to save resource');
        }
      } finally {
        this.saving = false;
      }
    },
    deleteResource(resource) {
      this.resourceToDelete = resource;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        await axios.delete(`/api/resources/admin/${this.resourceToDelete._id}`);
        if (this.$toast && this.$toast.success) {
          this.$toast.success('Resource deleted successfully');
        }
        this.deleteDialog = false;
        this.resourceToDelete = null;
        this.loadResources();
      } catch (error) {
        console.error('Error deleting resource:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error((error.response && error.response.data && error.response.data.error) || 'Failed to delete resource');
        }
      } finally {
        this.deleting = false;
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

